import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Plus,
  Pencil,
  Trash2,
  BookOpen,
  Tag,
  Star,
  History,
  Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  getHomeSections,
  createHomeSection,
  updateHomeSection,
  deleteHomeSection,
  reorderHomeSections,
  type HomeSection,
  type HomeSectionInput,
  type SectionType,
} from "@/services/homeSectionsService";
import { getContentListData } from "@/services/contentService";
import type { ContentItem } from "@/types/content";

// ─── tipos e íconos ────────────────────────────────────────────────────────────

const TYPE_META: Record<SectionType, { label: string; icon: React.ElementType; color: string }> = {
  manual: { label: "Manual", icon: BookOpen, color: "bg-blue-100 text-blue-700" },
  category: { label: "Categoría", icon: Tag, color: "bg-purple-100 text-purple-700" },
  hero: { label: "Hero", icon: Star, color: "bg-amber-100 text-amber-700" },
  continue_watching: { label: "Continúa", icon: History, color: "bg-green-100 text-green-700" },
};

// ─── fila sortable ─────────────────────────────────────────────────────────────

function SectionRow({
  section,
  onEdit,
  onDelete,
  onToggleVisible,
}: {
  section: HomeSection;
  onEdit: (s: HomeSection) => void;
  onDelete: (id: string) => void;
  onToggleVisible: (s: HomeSection) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = TYPE_META[section.type];
  const Icon = meta.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>

      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate">{section.title}</p>
        {section.subtitle && (
          <p className="text-xs text-muted-foreground truncate">{section.subtitle}</p>
        )}
      </div>

      <Badge variant="secondary" className={`text-xs shrink-0 ${meta.color}`}>
        {meta.label}
      </Badge>

      <Switch
        checked={section.visible}
        onCheckedChange={() => onToggleVisible(section)}
        title={section.visible ? "Ocultar" : "Mostrar"}
      />

      <Button variant="ghost" size="icon" onClick={() => onEdit(section)}>
        <Pencil className="h-4 w-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive"
        onClick={() => onDelete(section.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ─── editor de sección ─────────────────────────────────────────────────────────

const EMPTY_FORM: HomeSectionInput = {
  sort_order: 0,
  visible: true,
  type: "manual",
  title: "",
  subtitle: null,
  payload: {},
};

function SectionEditor({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: HomeSection | null;
  onClose: () => void;
  onSave: (input: HomeSectionInput) => void;
}) {
  const isNew = initial === null;
  const [form, setForm] = useState<HomeSectionInput>(() =>
    initial
      ? {
          sort_order: initial.sort_order,
          visible: initial.visible,
          type: initial.type,
          title: initial.title,
          subtitle: initial.subtitle,
          payload: initial.payload,
        }
      : EMPTY_FORM
  );
  const [contentSearch, setContentSearch] = useState("");

  const { data: contentData } = useQuery({
    queryKey: ["content-list-data"],
    queryFn: getContentListData,
  });
  const allContent = contentData?.contentItems ?? [];
  const published = allContent.filter((c) => c.status === "published");
  const filtered = published.filter(
    (c) =>
      !contentSearch ||
      c.title.toLowerCase().includes(contentSearch.toLowerCase()) ||
      c.author.toLowerCase().includes(contentSearch.toLowerCase())
  );

  // Helpers de payload
  const selectedIds: string[] = form.payload.content_ids ?? [];
  const toggleContentId = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setForm((f) => ({ ...f, payload: { ...f.payload, content_ids: next } }));
  };

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nueva sección" : "Editar sección"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Tipo */}
          <div className="space-y-1">
            <Label>Tipo</Label>
            <Select
              value={form.type}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, type: v as SectionType, payload: {} }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual — títulos elegidos a mano</SelectItem>
                <SelectItem value="category">Categoría — filtro por categoría</SelectItem>
                <SelectItem value="hero">Hero — portada grande de 1 título</SelectItem>
                <SelectItem value="continue_watching">Continúa — retoma del usuario</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Título */}
          <div className="space-y-1">
            <Label>Título</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ej: Lo nuevo esta semana"
            />
          </div>

          {/* Subtítulo */}
          <div className="space-y-1">
            <Label>Subtítulo (opcional)</Label>
            <Input
              value={form.subtitle ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, subtitle: e.target.value || null }))
              }
              placeholder="Descripción corta de la sección"
            />
          </div>

          {/* Payload según tipo */}
          {form.type === "manual" && (
            <div className="space-y-2">
              <Label>Títulos seleccionados ({selectedIds.length})</Label>
              <Input
                placeholder="Buscar por título o autor..."
                value={contentSearch}
                onChange={(e) => setContentSearch(e.target.value)}
              />
              <div className="max-h-52 overflow-y-auto rounded-lg border divide-y">
                {filtered.length === 0 && (
                  <p className="p-3 text-sm text-muted-foreground text-center">
                    Sin resultados
                  </p>
                )}
                {filtered.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleContentId(c.id)}
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm flex-1 min-w-0 truncate">
                      {c.title}
                      <span className="text-muted-foreground ml-1">· {c.author}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.type === "category" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Categoría (slug)</Label>
                <Input
                  value={form.payload.category ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      payload: { ...f.payload, category: e.target.value },
                    }))
                  }
                  placeholder="Ej: formacion, devocional, ninos"
                />
              </div>
              <div className="space-y-1">
                <Label>Límite de títulos</Label>
                <Input
                  type="number"
                  min={2}
                  max={12}
                  value={form.payload.limit ?? 6}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      payload: { ...f.payload, limit: Number(e.target.value) },
                    }))
                  }
                />
              </div>
            </div>
          )}

          {form.type === "hero" && (
            <div className="space-y-2">
              <Label>Título destacado</Label>
              <Input
                placeholder="Buscar por título o autor..."
                value={contentSearch}
                onChange={(e) => setContentSearch(e.target.value)}
              />
              <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
                {filtered.length === 0 && (
                  <p className="p-3 text-sm text-muted-foreground text-center">
                    Sin resultados
                  </p>
                )}
                {filtered.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50"
                  >
                    <input
                      type="radio"
                      name="hero-content"
                      checked={form.payload.content_id === c.id}
                      onChange={() =>
                        setForm((f) => ({
                          ...f,
                          payload: { content_id: c.id },
                        }))
                      }
                      className="h-4 w-4"
                    />
                    <span className="text-sm flex-1 min-w-0 truncate">
                      {c.title}
                      <span className="text-muted-foreground ml-1">· {c.author}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Visibilidad */}
          <div className="flex items-center gap-3">
            <Switch
              checked={form.visible}
              onCheckedChange={(v) => setForm((f) => ({ ...f, visible: v }))}
              id="visible-toggle"
            />
            <Label htmlFor="visible-toggle">Visible en la app</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!form.title.trim()}>
            {isNew ? "Crear" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── vista previa del teléfono ────────────────────────────────────────────────

// colores reales del app mobile
const APP_BG = "#F8FAF8";
const APP_TEXT = "#1C2B22";
const APP_MUTED = "#5D6D63";
const APP_PRIMARY = "#1F5A43";
const APP_CARD_BG = "#FFFCF8";
const APP_BORDER = "#E7DED3";

function PreviewCard({ item }: { item: ContentItem }) {
  return (
    <div className="shrink-0 w-24 flex flex-col gap-1.5">
      <div
        className="w-24 overflow-hidden"
        style={{ aspectRatio: "0.74" }}
      >
        {item.cover_image_url ? (
          <img src={item.cover_image_url} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-[9px] font-extrabold" style={{ color: APP_PRIMARY }}>SDV</span>
          </div>
        )}
      </div>
      <p className="text-[9px] leading-tight line-clamp-2 font-bold" style={{ color: APP_TEXT }}>{item.title}</p>
      <p className="text-[8px] line-clamp-1" style={{ color: APP_MUTED }}>{item.author}</p>
    </div>
  );
}

function PreviewSection({ section, allContent }: { section: HomeSection; allContent: ContentItem[] }) {
  // Hero: gradiente verde con texto + portada (igual que featuredCard del app)
  if (section.type === "hero") {
    const item = allContent.find((c) => c.id === section.payload.content_id);
    return (
      <div
        className="shrink-0 rounded-3xl p-4 flex flex-row gap-3 items-stretch"
        style={{ background: "linear-gradient(135deg, #234F3C, #1E3E30)", minHeight: 100 }}
      >
        <div className="flex-1 flex flex-col justify-between gap-1 min-w-0">
          <p className="text-[8px] font-bold uppercase tracking-wider" style={{ color: "#BFD6C8" }}>{section.title}</p>
          <p className="text-[11px] font-extrabold leading-tight line-clamp-2" style={{ color: "#FFFDF9" }}>
            {item?.title ?? "Sin título"}
          </p>
          {section.subtitle && (
            <p className="text-[9px] line-clamp-2 leading-tight" style={{ color: "#DDEBDD" }}>{section.subtitle}</p>
          )}
          <p className="text-[8px] font-semibold" style={{ color: "#BFD6C8" }}>{item?.author ?? ""}</p>
        </div>
        {item?.cover_image_url ? (
          <img
            src={item.cover_image_url}
            alt={item.title}
            className="shrink-0 object-cover"
            style={{ width: 64, height: "100%", minHeight: 80 }}
          />
        ) : (
          <div className="shrink-0 flex items-center justify-center" style={{ width: 64, minHeight: 80, backgroundColor: "#D8E7DE" }}>
            <BookOpen className="h-5 w-5 opacity-40" />
          </div>
        )}
      </div>
    );
  }

  // Continue watching: tarjeta crema con barra de progreso
  if (section.type === "continue_watching") {
    return (
      <div
        className="shrink-0 rounded-3xl p-4 flex flex-col gap-2 border"
        style={{ backgroundColor: APP_CARD_BG, borderColor: APP_BORDER }}
      >
        <div className="flex justify-between items-center">
          <p className="text-[8px] font-bold uppercase tracking-wider" style={{ color: APP_PRIMARY }}>{section.title}</p>
          <p className="text-[8px] font-bold" style={{ color: APP_PRIMARY }}>Abrir</p>
        </div>
        <p className="text-[10px] font-extrabold leading-tight line-clamp-2" style={{ color: APP_TEXT }}>Retoma donde lo dejaste</p>
        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#DED6CB" }}>
          <div className="h-full rounded-full" style={{ width: "46%", backgroundColor: APP_PRIMARY }} />
        </div>
        <p className="text-[8px]" style={{ color: APP_MUTED }}>Audio • en progreso</p>
      </div>
    );
  }

  let items: ContentItem[] = [];
  if (section.type === "manual") {
    const ids = section.payload.content_ids ?? [];
    items = ids.map((id) => allContent.find((c) => c.id === id)).filter(Boolean) as ContentItem[];
  } else if (section.type === "category") {
    items = allContent
      .filter((c) => c.category === section.payload.category && c.status === "published")
      .slice(0, section.payload.limit ?? 6);
  }

  return (
    <div className="shrink-0 flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <p className="text-[11px] font-extrabold" style={{ color: APP_TEXT }}>{section.title}</p>
        {section.subtitle && <p className="text-[9px]" style={{ color: APP_MUTED }}>{section.subtitle}</p>}
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl flex items-center justify-center p-4 border border-dashed" style={{ borderColor: APP_BORDER }}>
          <p className="text-[9px]" style={{ color: APP_MUTED }}>Sin títulos asignados</p>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-hidden">
          {items.slice(0, 3).map((item) => (
            <PreviewCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

// Slider "Recomendados" — cards con gradiente verde idénticas al app
function FeaturedSlider({ allContent }: { allContent: ContentItem[] }) {
  const slides = allContent.filter((c) => c.status === "published").slice(0, 4);
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => {
      // fade out → cambiar → fade in
      setVisible(false);
      setTimeout(() => {
        setActive((i) => (i + 1) % slides.length);
        setVisible(true);
      }, 300);
    }, 2800);
    return () => clearInterval(t);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div className="mx-3 rounded-3xl flex items-center justify-center shrink-0"
        style={{ height: 120, background: "linear-gradient(135deg, #234F3C, #1E3E30)" }}>
        <p className="text-[9px] opacity-50" style={{ color: "#FFFDF9" }}>Sin contenido publicado</p>
      </div>
    );
  }

  const cur = slides[active];
  return (
    <div className="px-3 flex flex-col gap-2 shrink-0">
      <div className="flex items-center gap-1">
        <p className="text-[10px] font-extrabold flex-1" style={{ color: APP_TEXT }}>Recomendados</p>
        <p className="text-[8px]" style={{ color: APP_MUTED }}>Selección destacada para empezar</p>
      </div>
      {/* card con gradiente + fade entre slides */}
      <div
        className="rounded-3xl p-4 flex flex-row items-stretch gap-3"
        style={{
          background: "linear-gradient(135deg, #234F3C, #1E3E30)",
          height: 110,
          opacity: visible ? 1 : 0,
          transition: "opacity 300ms ease-in-out",
        }}
      >
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <p className="text-[11px] font-extrabold leading-tight line-clamp-2" style={{ color: "#FFFDF9" }}>{cur.title}</p>
          {cur.subtitle && <p className="text-[9px] line-clamp-1" style={{ color: "#DDEBDD" }}>{cur.subtitle}</p>}
          <p className="text-[8px] font-semibold" style={{ color: "#BFD6C8" }}>{cur.author}</p>
        </div>
        {cur.cover_image_url ? (
          <img
            src={cur.cover_image_url}
            alt={cur.title}
            className="shrink-0 object-cover"
            style={{ width: 64, height: "100%" }}
          />
        ) : (
          <div className="shrink-0 flex items-center justify-center" style={{ width: 64, backgroundColor: "#D8E7DE" }}>
            <span className="text-[8px] font-extrabold" style={{ color: APP_PRIMARY }}>SDV</span>
          </div>
        )}
      </div>
      {/* dots */}
      {slides.length > 1 && (
        <div className="flex justify-center gap-1.5">
          {slides.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === active ? 18 : 6,
                backgroundColor: i === active ? APP_PRIMARY : "#C9D6CF",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HomePreview({ sections, allContent }: { sections: HomeSection[]; allContent: ContentItem[] }) {
  const visible = sections.filter((s) => s.visible);

  return (
    <div className="sticky top-6 flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs font-medium self-start">
        <Smartphone className="h-3.5 w-3.5" />
        Vista previa
      </div>

      {/* Phone frame */}
      <div
        className="relative w-[300px] h-[620px] rounded-[2.5rem] border-4 border-zinc-300 shadow-2xl overflow-hidden flex flex-col"
        style={{ backgroundColor: APP_BG }}
      >
        {/* Notch */}
        <div className="shrink-0 flex justify-center pt-2" style={{ backgroundColor: APP_BG }}>
          <div className="w-16 h-1.5 bg-zinc-300 rounded-full" />
        </div>

        {/* Status bar */}
        <div className="shrink-0 h-5 flex items-center justify-between px-5" style={{ backgroundColor: APP_BG }}>
          <span className="text-[8px] font-medium" style={{ color: APP_MUTED }}>9:41</span>
          <div className="flex gap-1.5 items-center">
            <div className="flex gap-0.5 items-end h-2.5">
              {[3, 5, 7, 9].map((h, i) => (
                <div key={i} style={{ height: h, width: 2, backgroundColor: APP_MUTED, borderRadius: 1 }} />
              ))}
            </div>
            <div className="w-3.5 h-2 rounded-sm flex p-0.5" style={{ border: `1px solid ${APP_MUTED}` }}>
              <div className="flex-1 rounded-sm" style={{ backgroundColor: APP_MUTED }} />
            </div>
          </div>
        </div>

        {/* App header */}
        <div className="shrink-0 px-4 pt-1 pb-2" style={{ backgroundColor: APP_BG }}>
          <p className="font-extrabold text-sm" style={{ color: APP_TEXT }}>Inicio</p>
        </div>

        {/* Scroll area completa */}
        <div
          className="flex-1 overflow-y-auto flex flex-col gap-4 pb-4"
          style={{ backgroundColor: APP_BG }}
        >
          {/* Slider "Recomendados" — siempre visible */}
          <FeaturedSlider allContent={allContent} />

          {/* Secciones configuradas */}
          {visible.length === 0 ? (
            <div className="flex-1 flex items-center justify-center px-6">
              <p className="text-[10px] text-center leading-relaxed" style={{ color: APP_MUTED }}>
                Agrega secciones visibles<br />para ver la previsualización
              </p>
            </div>
          ) : (
            <div className="px-3 flex flex-col gap-4">
              {visible.map((s) => (
                <PreviewSection key={s.id} section={s} allContent={allContent} />
              ))}
            </div>
          )}
        </div>

        {/* Bottom nav bar */}
        <div
          className="shrink-0 h-14 flex items-center justify-around px-2 pb-2 border-t"
          style={{ backgroundColor: "#FFFFFF", borderColor: APP_BORDER }}
        >
          {[
            { label: "Inicio", active: true },
            { label: "Bibliot.", active: false },
            { label: "Buscar", active: false },
            { label: "Perfil", active: false },
          ].map((tab) => (
            <div key={tab.label} className="flex flex-col items-center gap-1">
              <div
                className="w-5 h-0.5 rounded-full"
                style={{ backgroundColor: tab.active ? APP_PRIMARY : "transparent" }}
              />
              <span
                className="text-[8px]"
                style={{ color: tab.active ? APP_PRIMARY : APP_MUTED, fontWeight: tab.active ? 700 : 400 }}
              >
                {tab.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── página principal ──────────────────────────────────────────────────────────

export default function HomeSectionsPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<HomeSection | null | "new">(null);

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ["home-sections"],
    queryFn: getHomeSections,
  });

  const { data: contentData } = useQuery({
    queryKey: ["content-list-data"],
    queryFn: getContentListData,
  });
  const allContent = contentData?.contentItems ?? [];

  const sensors = useSensors(useSensor(PointerSensor));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["home-sections"] });

  const createMutation = useMutation({
    mutationFn: (input: HomeSectionInput) =>
      createHomeSection({ ...input, sort_order: sections.length }),
    onSuccess: () => { invalidate(); setEditing(null); toast({ title: "Sección creada" }); },
    onError: () => toast({ title: "Error al crear", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<HomeSectionInput> }) =>
      updateHomeSection(id, input),
    onSuccess: () => { invalidate(); setEditing(null); toast({ title: "Sección guardada" }); },
    onError: () => toast({ title: "Error al guardar", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHomeSection,
    onSuccess: () => { invalidate(); toast({ title: "Sección eliminada" }); },
    onError: () => toast({ title: "Error al eliminar", variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: reorderHomeSections,
    onSuccess: invalidate,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((s) => s.id === active.id);
    const newIndex = sections.findIndex((s) => s.id === over.id);
    const reordered = arrayMove(sections, oldIndex, newIndex);

    // Optimistic update vía query client
    qc.setQueryData(["home-sections"], reordered);
    reorderMutation.mutate(
      reordered.map((s, i) => ({ id: s.id, sort_order: i }))
    );
  };

  const handleSave = (input: HomeSectionInput) => {
    if (editing === "new") {
      createMutation.mutate(input);
    } else if (editing && editing !== "new") {
      updateMutation.mutate({ id: editing.id, input });
    }
  };

  const handleToggleVisible = (section: HomeSection) => {
    updateMutation.mutate({ id: section.id, input: { visible: !section.visible } });
  };

  return (
    <div className="flex gap-6 items-start">
      {/* columna izquierda: gestión de secciones */}
      <div className="flex-1 min-w-0 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Home — Secciones</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Arrastra para reordenar. Los cambios se reflejan en la app mobile al instante.
            </p>
          </div>
          <Button onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva sección
          </Button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Cargando...</p>
        ) : sections.length === 0 ? (
          <div className="rounded-xl border border-dashed p-12 text-center">
            <p className="text-muted-foreground text-sm">
              No hay secciones. Crea una para empezar a configurar el Home.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4 mr-2" /> Primera sección
            </Button>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {sections.map((section) => (
                  <SectionRow
                    key={section.id}
                    section={section}
                    onEdit={setEditing}
                    onDelete={(id) => deleteMutation.mutate(id)}
                    onToggleVisible={handleToggleVisible}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* columna derecha: vista previa */}
      <HomePreview sections={sections} allContent={allContent} />

      <SectionEditor
        key={editing === "new" ? "new" : (editing as HomeSection | null)?.id ?? "closed"}
        open={editing !== null}
        initial={editing === "new" ? null : (editing as HomeSection | null)}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />
    </div>
  );
}
